import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import apiClient from "@/lib/api-client";
import type {
  UnmatchedPolicy,
  ZoteroLibraryItem,
  ZoteroSourceResponse,
} from "@/types/zoteroSync";

const QK_SOURCE = ["zotero-source"];

export function useZoteroSource() {
  return useQuery({
    queryKey: QK_SOURCE,
    queryFn: () =>
      apiClient.get("api/me/zotero-source").json<ZoteroSourceResponse | null>(),
  });
}

export function useValidateApiKey() {
  return useMutation({
    mutationFn: (apiKey: string) =>
      apiClient
        .post("api/me/zotero-source/web-api/validate", {
          json: { api_key: apiKey },
        })
        .json<{ libraries: ZoteroLibraryItem[] }>(),
  });
}

export interface ZoteroCollectionItem {
  key: string;
  name: string;
  parent_key: string | null;
}

export function useListCollections() {
  return useMutation({
    mutationFn: (vars: {
      api_key: string;
      library_type: "user" | "group";
      library_id: string;
    }) =>
      apiClient
        .post("api/me/zotero-source/web-api/list-collections", { json: vars })
        .json<{ collections: ZoteroCollectionItem[] }>(),
  });
}

export function useMyCollections(enabled: boolean) {
  return useQuery({
    queryKey: ["zotero-my-collections"],
    queryFn: () =>
      apiClient
        .get("api/me/zotero-source/web-api/my-collections")
        .json<{ collections: ZoteroCollectionItem[] }>(),
    enabled,
  });
}

export function useSelectLibrary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      api_key: string;
      library_type: "user" | "group";
      library_id: string;
      // Empty / omitted = watch the whole library. A non-empty array
      // scopes the poller to those collections (see
      // services/zotero_poller.py:poll_user_once).
      watched_collection_ids?: string[];
    }) =>
      apiClient
        .post("api/me/zotero-source/web-api/select", { json: vars })
        .json<ZoteroSourceResponse>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_SOURCE }),
  });
}

export function useUpdateWatchedCollections() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (watched_collection_ids: string[]) =>
      apiClient
        .put("api/me/zotero-source/web-api/watched-collections", {
          json: { watched_collection_ids },
        })
        .json<ZoteroSourceResponse>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_SOURCE }),
  });
}

export function useSetUnmatchedPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (policy: UnmatchedPolicy) =>
      apiClient
        .put("api/me/zotero-source/web-api/unmatched-policy", {
          json: { unmatched_policy: policy },
          // LLM/batch-style operation budget; matches CLAUDE.md rule #9.
          timeout: 120_000,
        })
        .json<ZoteroSourceResponse>(),
    onMutate: async (policy) => {
      await qc.cancelQueries({ queryKey: QK_SOURCE });
      const prev = qc.getQueryData<ZoteroSourceResponse | null>(QK_SOURCE);
      if (prev) {
        qc.setQueryData<ZoteroSourceResponse>(QK_SOURCE, {
          ...prev,
          unmatched_policy: policy,
          // Backend zeroes the counter on skip→tray. Mirror that
          // optimistically so the UI doesn't flash a stale number.
          unmatched_skipped_count:
            prev.unmatched_policy === "skip" && policy === "tray"
              ? 0
              : prev.unmatched_skipped_count,
        });
      }
      return { prev };
    },
    onError: (_err, _policy, ctx) => {
      if (ctx?.prev) qc.setQueryData(QK_SOURCE, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: QK_SOURCE }),
  });
}

export function usePollNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient.post("api/zotero-sync/poll-now").json<{ queued: boolean }>(),
    onSuccess: () => {
      // poll-status flips to running=true; usePollStatus's 2s polling
      // detects the running→idle transition and refetches events lists.
      qc.invalidateQueries({ queryKey: ["zotero-sync-poll-status"] });
      qc.invalidateQueries({ queryKey: ["zotero-sync-events", "matched"] });
      qc.invalidateQueries({ queryKey: ["zotero-sync-events", "unmatched"] });
    },
  });
}

interface PollStatusResponse {
  running: boolean;
  started_at: string | null;
  completed_at: string | null;
}

/**
 * Tracks the most-recent focused-poll lifecycle for the current user.
 *
 * Polls /api/zotero-sync/poll-status every 2s while a run is in flight,
 * goes idle when it isn't. On the running→idle transition, invalidates
 * the events lists so the new unmatched/matched rows surface within ~2s
 * of the backend finishing — instead of waiting up to 30s for the next
 * `refetchInterval` tick.
 */
export function usePollStatus() {
  const qc = useQueryClient();
  const wasRunningRef = useRef(false);

  const query = useQuery({
    queryKey: ["zotero-sync-poll-status"],
    queryFn: () =>
      apiClient
        .get("api/zotero-sync/poll-status")
        .json<PollStatusResponse>(),
    refetchInterval: (q) =>
      q.state.data?.running ? 2_000 : false,
  });

  useEffect(() => {
    const isRunning = query.data?.running ?? false;
    if (wasRunningRef.current && !isRunning) {
      qc.invalidateQueries({
        queryKey: ["zotero-sync-events", "unmatched"],
      });
      qc.invalidateQueries({
        queryKey: ["zotero-sync-events", "matched"],
      });
    }
    wasRunningRef.current = isRunning;
  }, [query.data?.running, qc]);

  return query;
}

export function useDisconnectSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.delete("api/me/zotero-source"),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_SOURCE }),
  });
}

export function useLibraryUrlPattern() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["library-url-pattern"],
    queryFn: () =>
      apiClient
        .get("api/me/library-url-pattern")
        .json<{ pattern: string | null }>(),
  });
  const setMutation = useMutation({
    mutationFn: (pattern: string | null) =>
      apiClient
        .put("api/me/library-url-pattern", { json: { pattern } })
        .json<{ pattern: string | null }>(),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["library-url-pattern"] }),
  });
  return { query, setMutation };
}
